import * as R from 'ramda';
import * as Functional from '../functional';
import LatLng from './latlng';
import * as Theme from './theme';

const hasText = feature => feature.properties.text;
const hasBadge = feature => feature.properties.showBadge;

const isPolygon = feature => feature.geometry.type === 'Polygon';
const isLineString = feature => feature.geometry.type === 'LineString';
const isSymbol = feature => feature.geometry.type === 'Point';
const isTouch = feature => feature.properties.isTouch;

// linear ring is an array of coordinates where the last coordinate is identical to the first
const centerOfLinearRing = linearRing => {
    const lngs = R.pluck(0, R.tail(linearRing)); // don't double-count first
    const lats = R.pluck(1, R.tail(linearRing)); // don't double-count first
    return LatLng.fromArray([R.mean(lngs), R.mean(lats)]);
};

const center = feature => {
    if (!feature) return;

    const coordinates = feature.geometry.coordinates;

    switch (feature.geometry.type) {
        case 'Point':
            return LatLng.fromArray(coordinates);
        case 'LineString':
            return centerOfLinearRing(coordinates);
        case 'Polygon':
            return centerOfLinearRing(coordinates[0]);
        default:
            throw new Error(`Feature.center not handling type ${feature.geometry.type}`);
    }
};

/**
 * Return the canonical names of every image in the GeoJson data
 * @sig imagesInGeoJson :: [Feature] -> [String]
 */
const categoriesForFeatures = features => {
    let categories = R.map(R.path(['properties', 'category']), features);
    categories = R.filter(R.identity, categories);
    return R.uniq(categories);
};

/**
 * Return the canonical names of every logo in the GeoJson data
 * @sig logosForFeatures :: [Feature] -> [String]
 */
const logosForFeatures = features => {
    let logos = R.map(R.path(['properties', 'logo']), features);
    logos = R.filter(R.identity, logos);
    return R.uniq(logos);
};

/*
 * Return the bounding rectangle of all the GeoJSON's features; return a structure with west, east, north and south
 * @sig boundingBoxOfFeatures :: GeoJson => { w:, e:, n:, s: }
 */
const boundingBoxOfFeatures = features => {
    const emptyBoundingBox = {
        w: Number.POSITIVE_INFINITY,
        e: Number.NEGATIVE_INFINITY,
        n: Number.NEGATIVE_INFINITY,
        s: Number.POSITIVE_INFINITY,
    };

    // Flatten a potentially nested array of coordinates; result alternates lng/lat
    const flattenCoordinates = coordinates =>
        R.is(Number, coordinates[0]) ? coordinates : R.chain(flattenCoordinates, coordinates);

    // expand the bounding box to include the boundingBoxOfFeatures of the feature
    const expandBoundigBoxToIncludeFeature = (boundingBox, feature) => {
        const coordinates = flattenCoordinates(feature.geometry.coordinates);
        const [lngs, lats] = Functional.splitAlternating(coordinates);

        const w = Math.min(boundingBox.w, Functional.min(lngs));
        const e = Math.max(boundingBox.e, Functional.max(lngs));
        const n = Math.max(boundingBox.n, Functional.max(lats));
        const s = Math.min(boundingBox.s, Functional.min(lats));
        return { w, e, n, s };
    };

    return R.reduce(expandBoundigBoxToIncludeFeature, emptyBoundingBox, features);
};

// ---------------------------------------------------------------------------------------------------------------------
// VMS update/fromData
// ---------------------------------------------------------------------------------------------------------------------

// :: String -> Boolean
const _themeHasNoSuchCategory = category => !category || category.length === 0;

const _categoryHasNoFillOrStroke = category => !category.fillColor || !category.strokeColor;

/*
 * Assuming the Theme has an entry for the Feature's category, enrich the Feature with attributes from the Theme
 */
const _addAttributesFromTheme = (theme, feature, attributes) => {
    const category = R.path(['properties', 'category'], feature);

    if (_themeHasNoSuchCategory(category)) return feature;

    const themeCategory = Theme.themeForCategory(theme, category);

    if (_categoryHasNoFillOrStroke(themeCategory)) return feature;

    const updates = R.pick(attributes, themeCategory);
    return Functional.mergeWithProp('properties', updates, feature);
};

/*
 * Update a features fill and stroke color based on the theme
 * @sig updateFeatureFromTheme :: Theme -> Feature -> Feature
 */
const updateFeatureFromTheme = theme => feature =>
    _addAttributesFromTheme(theme, feature, ['fillColor', 'strokeColor']);

/*
 * Update a feature text based on the entity.mapLabel or entity.name
 * @sig updateFeatureFromEntity :: entityIdNameMap -> Feature -> Feature
 */
const updateFeatureFromEntity = entityIdNameMap => feature => {
    const entityName = entityIdNameMap[R.path(['properties', 'id'], feature)];
    return entityName ? R.assocPath(['properties', 'text'], entityName, feature) : feature;
};

/*
 * Update the Feature with new attributes
 * @sig update :: ({Theme, Feature, String, String, LatLng, Boolean, Boolean }) -> Feature
 *
 * Note: category, name, latLng, showBadge and showText are optional
 */
const update = ({ theme, feature, showBadge = false, showText = false, category, text, latLng }) => {
    /*
     * Add attributes for text from the theme to the feature (assuming the theme knows about the Feature's category)
     * @sig _updateSymbolAttributes :: (Theme, Feature, Boolean, Boolean, String, LatLng) -> Feature
     */
    const _updateSymbolAttributes = existingText => {
        if (!showBadge && !showText) throw new Error('Feature.update: showBadge and/or showText has to be set');

        feature = R.assocPath(['properties', 'showBadge'], !!showBadge, feature);

        if (showText && existingText) feature = R.assocPath(['properties', 'text'], existingText, feature);
        if (!showText) feature = Functional.omitAtPath(['properties', 'text'], feature);
        // else leave text alone...

        if (!R.isNil(latLng)) feature = R.assocPath(['geometry', 'coordinates'], [latLng.lng, latLng.lat], feature);

        return _addAttributesFromTheme(theme, feature, ['textColor', 'fontName', 'fontSize']);
    };

    /*
     * Add attributes for lines from the theme to the feature (assuming the theme knows about the Feature's category)
     * @sig _updateLineAttributes :: (Theme, Feature) -> Feature
     */
    const _updateLineAttributes = () =>
        _addAttributesFromTheme(theme, feature, ['strokeColor', 'strokeWidth', 'strokeAlign']);

    /*
     * Add attributes for a polygon from the theme to the feature (assuming the theme knows about the
     * Feature's category)
     * @sig _updatePolygonAttributes :: (Theme, Feature) -> Feature
     */
    const _updatePolygonAttributes = () => {
        const props = isTouch(feature) ? ['fillColor'] : ['fillColor', 'strokeColor', 'strokeWidth', 'strokeAlign'];
        return _addAttributesFromTheme(theme, feature, props);
    };

    text = showText ? text : undefined;

    // always need to update the category if it changed
    feature = Functional.assocPathUnlessNil(['properties', 'category'], category, feature);

    if (isSymbol(feature)) return _updateSymbolAttributes(text);
    if (isPolygon(feature)) return _updatePolygonAttributes();
    if (isLineString(feature)) return _updateLineAttributes();

    throw new Error('feature is not polygon, line, text or badge');
};

/*
 * Create a Feature from data (this is uncommon; usually we receive Features pre-formed from a store)
 */
const symbolFeatureFromData = ({
    theme,
    entityId,
    featureId,
    category,
    text,
    latLng,
    features,
    aiLayer = 'poi',
    showBadge = false,
    showText = false,
}) => {
    if (!showBadge && !showText) throw new Error('symbolFeatureFromData: showBadge and/or text must be defined');

    if (!featureId && features) featureId = createFeatureId(features, entityId);

    let feature = {
        id: featureId,
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [latLng.lng, latLng.lat] },
        properties: {
            type: 'Text',
            align: 'center',
            id: entityId,
            subid: featureId,
            category,
            aiLayer,
        },
    };

    if (showBadge) feature = R.assocPath(['properties', 'showBadge'], true, feature);

    if (showText) {
        feature = R.assocPath(['properties', 'text'], text, feature);
        feature = _addAttributesFromTheme(theme, feature, ['textColor', 'fontName', 'fontSize']);
    }

    return feature;
};

const createFeatureId = (features, entityId) => {
    const pluckFeatureIds = R.map(R.path(['properties', 'subid']));
    const otherFeatureIdsForEntity = pluckFeatureIds(R.filter(f => f.properties.id === entityId, features));

    if (otherFeatureIdsForEntity.length === 0) {
        // there are NO features with subids yet, so just append 'a'
        return `${entityId}a`;
    }

    // find the subid with the highest suffix and add 1 to that (eg. if 14c exists return 14d)
    const lastExistingSubid = R.last(otherFeatureIdsForEntity.sort());
    const [prefix, suffix] = [R.dropLast(1, lastExistingSubid), R.takeLast(1, lastExistingSubid)];
    return prefix + String.fromCharCode(suffix.charCodeAt(0) + 1);
};

/*
 * A GeoJSON feature can have a root-level 'id' property; here we set it from the embedded subid property
 * @sig promoteIdFromSubidProperty :: Feature -> Feature
 */
const promoteIdFromSubidProperty = feature => R.assoc('id', feature.properties.subid, feature);

/*
 * Separate a feature into polygons and notPolygons
 * @sig groupByPolygon :: [Feature] -> { polygon: [Feature], notPolygon: [Feature] }
 */
const groupByPolygon = features => R.groupBy(f => (isPolygon(f) ? 'polygon' : 'notPolygon'), features);

/*
 * Update a polygon Feature by transferring data from an Entity
 * @sig updatePolygonFeatureForEntity : (Entity, Feature) -> Feature
 */
const updatePolygonFeatureForEntity = (entity, feature, theme) => {
    const updateInfo = {
        feature,
        category: entity.category,
        theme,
    };

    feature = update(updateInfo);
    feature = R.assocPath(['properties', 'id'], entity.id, feature);
    feature = Functional.omitAtPath(['properties', 'subid'], feature);
    feature = R.omit(['$loki'], feature);
    return feature;
};

const openLocationBody = (entity, featuresForEntity, oldFeature) => {
    featuresForEntity = R.map(f => {
        f = R.omit(['$loki', 'meta', 'id'], f);
        f.properties = R.omit(
            ['type', 'fontName', 'fontSize', 'levelId', 'ordinalId', 'structureId', 'venueId'],
            f.properties,
        );
        return f;
    }, featuresForEntity);

    return {
        entity,
        features: featuresForEntity,
        oldFeatureId: oldFeature.properties.id,
    };
};

export {
    center,
    categoriesForFeatures,
    logosForFeatures,
    boundingBoxOfFeatures,
    hasBadge,
    hasText,
    isSymbol,
    isPolygon,
    isLineString,
    isTouch,
    updateFeatureFromTheme,
    updateFeatureFromEntity,
    promoteIdFromSubidProperty,
    groupByPolygon,
    updatePolygonFeatureForEntity,
    openLocationBody,

    // for VMS only
    symbolFeatureFromData,
    update,
};
